/**
 * W3-M3 — canonical capability registry (Bundle 3 M3).
 *
 * Spec: 02_specs/14_m3_iframe_bridge_version_capability_negotiation_2026-05-31.md §5.4.
 *
 * The registry is the shell's CANONICAL declaration of capability tags it
 * supports + their hardness classification.
 *
 * Registry extension rule (spec §5.4): every new capability tag MUST be appended
 * to this table in a future spec amendment AND announced in shell + iframe code.
 * No silent additions.
 *
 * Determinism + serializability: `freeze` is applied so consumers cannot mutate
 * the registry at runtime. JSON serialization round-trips deterministically.
 */

export type CapabilityTag =
  // L2 baseline (hard-required for any iframe).
  | 'bridge.heartbeat.v1'
  | 'bridge.error.v1'
  // H1 navigation highlight (Bundle 2 W2-A).
  | 'navigation.highlight.v1'
  // H2 capability state (Bundle 2 W2-A).
  | 'capability.state.v1'
  // H3 askai writeback (Bundle 2 W2-B).
  | 'askai.writeback.v1'
  // H4 scout iframe audit (Bundle 2 W2-C).
  | 'scout.audit.v1'
  // M1 activity move (Bundle 3 W3-M1).
  | 'activity.move.v1';

/** Hardness classification per M3 spec §5.4 + spec §4 outcome rules. */
export type CapabilityHardness =
  /** Absent → DEGRADE (graceful no-op or hidden feature). */
  | 'soft'
  /** Absent → WARN if a workaround exists (e.g. read-only); else BLOCK. */
  | 'hard_with_workaround'
  /** Absent → BLOCK + H4 fallback F5. */
  | 'hard_required';

export interface CapabilityDefinition {
  tag: CapabilityTag;
  owner_spec: string;
  hardness: CapabilityHardness;
  description: string;
  fallback_path_when_absent?: string;
}

/** Shell M3 implementation version. */
export const SHELL_BRIDGE_VERSION = '2.0.0';

/** Canonical capability registry. Frozen at module load; consumers must not mutate. */
export const CAPABILITY_REGISTRY_V2: ReadonlyArray<Readonly<CapabilityDefinition>> = Object.freeze([
  Object.freeze({
    tag: 'bridge.heartbeat.v1',
    owner_spec: 'L2 spec 08 §6',
    hardness: 'hard_required',
    description: 'Liveness heartbeats — required by every iframe.',
  }),
  Object.freeze({
    tag: 'bridge.error.v1',
    owner_spec: 'L2 spec 08 §6',
    hardness: 'hard_required',
    description: 'Structured error reporting — required by every iframe.',
  }),
  Object.freeze({
    tag: 'navigation.highlight.v1',
    owner_spec: 'H1 spec 09',
    hardness: 'soft',
    description: 'Cross-iframe nav highlight sync; degrades to local-only.',
  }),
  Object.freeze({
    tag: 'capability.state.v1',
    owner_spec: 'H2 spec 09',
    hardness: 'soft',
    description: 'Capability-state sharing; shell-only when absent.',
  }),
  Object.freeze({
    tag: 'askai.writeback.v1',
    owner_spec: 'H3 spec 10',
    hardness: 'hard_with_workaround',
    description: 'AskAI writeback — read-only fallback when absent.',
  }),
  Object.freeze({
    tag: 'scout.audit.v1',
    owner_spec: 'H4 spec 11',
    hardness: 'hard_required',
    description: 'Scout iframe audit channel — BLOCK + H4 fallback F5 when absent.',
    fallback_path_when_absent: 'F5',
  }),
  Object.freeze({
    tag: 'activity.move.v1',
    owner_spec: 'M1 spec 12',
    hardness: 'soft',
    description: 'Activity move acknowledgement; non-bridge fallback when absent.',
  }),
]);

/** O(1) lookup; built once at module load. */
const REGISTRY_BY_TAG: ReadonlyMap<CapabilityTag, Readonly<CapabilityDefinition>> = new Map(
  CAPABILITY_REGISTRY_V2.map((def) => [def.tag, def] as const),
);

export function getCapability(tag: CapabilityTag): Readonly<CapabilityDefinition> | null {
  return REGISTRY_BY_TAG.get(tag) ?? null;
}

export function allCapabilityTags(): ReadonlyArray<CapabilityTag> {
  return CAPABILITY_REGISTRY_V2.map((d) => d.tag);
}

export function hardRequiredCapabilities(): ReadonlyArray<CapabilityTag> {
  return CAPABILITY_REGISTRY_V2.filter((d) => d.hardness === 'hard_required').map((d) => d.tag);
}

export function hardWithWorkaroundCapabilities(): ReadonlyArray<CapabilityTag> {
  return CAPABILITY_REGISTRY_V2.filter((d) => d.hardness === 'hard_with_workaround').map(
    (d) => d.tag,
  );
}

export function softCapabilities(): ReadonlyArray<CapabilityTag> {
  return CAPABILITY_REGISTRY_V2.filter((d) => d.hardness === 'soft').map((d) => d.tag);
}

/** Deterministic + serializable snapshot used by `GET /bridge/version` clients
 *  AND by handshake.ts when packing the outbound handshake message. Output keys
 *  are sorted so `JSON.stringify` is stable across runs. */
export function registrySnapshot(): {
  shell_version: string;
  shell_capabilities: ReadonlyArray<CapabilityTag>;
  hard_required: ReadonlyArray<CapabilityTag>;
  hard_with_workaround: ReadonlyArray<CapabilityTag>;
  soft: ReadonlyArray<CapabilityTag>;
} {
  return Object.freeze({
    shell_version: SHELL_BRIDGE_VERSION,
    shell_capabilities: allCapabilityTags(),
    hard_required: hardRequiredCapabilities(),
    hard_with_workaround: hardWithWorkaroundCapabilities(),
    soft: softCapabilities(),
  });
}
